import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { LogOut, Copy, BadgeCheck, AlertCircle, Sun, Moon, Monitor } from "lucide-react";
import { format } from "date-fns";
import { useTheme } from "next-themes";

import { PLAN_LABELS } from "@/lib/plans";

const THEME_OPTIONS = [
  { value: "light", label: "Light", icon: Sun },
  { value: "dark", label: "Dark", icon: Moon },
  { value: "system", label: "Match my computer", icon: Monitor },
];

/** Light / Dark / Match my computer, as a segmented control. */
function ThemeChoice() {
  const { theme, setTheme } = useTheme();
  const current = theme || "system";
  return (
    <div role="radiogroup" aria-label="Theme" className="inline-flex flex-wrap gap-1 rounded-md border border-border bg-surface-inset p-1">
      {THEME_OPTIONS.map(({ value, label, icon: Icon }) => (
        <button key={value} type="button" role="radio" aria-checked={current === value}
          onClick={() => setTheme(value)} data-testid={`appearance-${value}`}
          className={`flex items-center gap-1.5 rounded-sm px-3 py-1.5 text-sm transition-colors ${
            current === value ? "bg-surface font-medium text-foreground shadow-card" : "text-muted-foreground hover:text-foreground"
          }`}>
          <Icon className="h-3.5 w-3.5" /> {label}
        </button>
      ))}
    </div>
  );
}

function initialsFrom(name, email) {
  const src = (name || "").trim() || (email || "").trim();
  if (!src) return "?";
  const parts = src.split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return src.slice(0, 2).toUpperCase();
}

export default function Profile() {
  const { user, refreshUser, logout } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState(user?.name || "");
  const [company, setCompany] = useState(user?.company || "");
  const [next, setNext] = useState("");
  const [saving, setSaving] = useState(false);

  // Loading state — user is null while the session/profile hydrates.
  if (!user || user === false) {
    return (
      <div className="flex h-full flex-col overflow-hidden">
        <header className="flex items-center justify-between border-b border-border px-6 py-4 md:px-10">
          <div>
            <div className="eyebrow">Account</div>
            <h1 className="font-display text-2xl tracking-tight">Profile & Settings</h1>
          </div>
        </header>
        <div className="flex-1 overflow-y-auto px-6 py-8 md:px-10 md:py-10">
          <div className="mx-auto w-full max-w-3xl space-y-4">
            <Skeleton className="h-24 w-full rounded-lg" />
            <Skeleton className="h-40 w-full rounded-lg" />
            <Skeleton className="h-40 w-full rounded-lg" />
          </div>
        </div>
      </div>
    );
  }

  const authUser = user.auth || {};
  const meta = authUser.user_metadata || {};
  const appMeta = authUser.app_metadata || {};
  const avatarUrl = meta.avatar_url || meta.picture || "";
  const providers = appMeta.providers || (appMeta.provider ? [appMeta.provider] : []);
  const isGoogle = providers.includes("google");
  const hasEmailLogin = providers.includes("email");
  const emailVerified = Boolean(authUser.email_confirmed_at);
  const memberSince = authUser.created_at || user.created_at;

  const nameDirty = name !== (user.name || "");
  const companyDirty = company !== (user.company || "");

  const saveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    const { error } = await supabase
      .from("profiles")
      .update({ name, company })
      .eq("id", user.id);
    if (error) {
      toast.error(error.message);
    } else {
      await refreshUser();
      toast.success("Profile updated");
    }
    setSaving(false);
  };

  const changePass = async (e) => {
    e.preventDefault();
    if (next.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setSaving(true);
    const { error } = await supabase.auth.updateUser({ password: next });
    if (error) {
      toast.error(error.message);
    } else {
      setNext("");
      toast.success(hasEmailLogin ? "Password updated" : "Password set — you can now sign in with email too");
    }
    setSaving(false);
  };

  const onLogout = async () => {
    await logout();
    navigate("/login");
  };

  const creditsRemaining = (user?.credits_total ?? 0) - (user?.credits_used ?? 0);
  const creditsPct = user?.credits_total
    ? Math.max(0, Math.min(100, (creditsRemaining / user.credits_total) * 100))
    : 0;
  const plan = user?.plan || "free";

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <header className="flex items-center justify-between border-b border-border px-6 py-4 md:px-10">
        <div>
          <div className="eyebrow">Account</div>
          <h1 className="font-display text-2xl tracking-tight">Profile & Settings</h1>
        </div>
        <Button variant="outline" className="gap-2" onClick={onLogout} data-testid="profile-logout-btn">
          <LogOut className="h-4 w-4" /> Log out
        </Button>
      </header>

      <div className="flex-1 overflow-y-auto px-6 py-8 md:px-10 md:py-10">
        <div className="mx-auto w-full max-w-3xl space-y-8">
          {/* ── Account header card ── */}
          <div className="flex items-center gap-4 rounded-xl border border-border bg-surface p-6 shadow-card" data-testid="profile-header-card">
            <Avatar className="h-16 w-16">
              {avatarUrl ? <AvatarImage src={avatarUrl} alt={user.name || "avatar"} /> : null}
              <AvatarFallback className="text-lg font-display">{initialsFrom(user.name, user.email)}</AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <div className="truncate font-display text-xl tracking-tight">{user.name || "Unnamed"}</div>
              <div className="truncate text-sm text-muted-foreground">{user.email}</div>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <Badge variant="secondary" data-testid="provider-badge">
                  {isGoogle ? "Google" : "Email"}
                </Badge>
                {emailVerified ? (
                  <span className="inline-flex items-center gap-1 text-xs text-success" data-testid="email-verified">
                    <BadgeCheck className="h-3.5 w-3.5" /> Verified
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-xs text-warning" data-testid="email-unverified">
                    <AlertCircle className="h-3.5 w-3.5" /> Unverified
                  </span>
                )}
                {memberSince && (
                  <span className="text-xs text-muted-foreground">
                    Member since {format(new Date(memberSince), "MMM yyyy")}
                  </span>
                )}
              </div>
            </div>
          </div>

          <Tabs defaultValue="user" className="w-full">
            <TabsList>
              <TabsTrigger value="user" data-testid="profile-tab-user">User</TabsTrigger>
              <TabsTrigger value="workspace" data-testid="profile-tab-workspace">Workspace</TabsTrigger>
              <TabsTrigger value="plan" data-testid="profile-tab-plan">Plan & Usage</TabsTrigger>
              <TabsTrigger value="api" data-testid="profile-tab-api">API</TabsTrigger>
            </TabsList>

            {/* ── User ── */}
            <TabsContent value="user" className="mt-6 space-y-8">
              <form onSubmit={saveProfile} className="space-y-4 rounded-xl border border-border bg-surface p-6 shadow-card" data-testid="profile-form">
                <div className="eyebrow">Personal</div>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <div>
                    <Label htmlFor="name">Name</Label>
                    <Input id="name" value={name} onChange={(e) => setName(e.target.value)} data-testid="profile-name-input" />
                  </div>
                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input id="email" value={user?.email || ""} disabled data-testid="profile-email-input" />
                  </div>
                </div>
                <Button type="submit" disabled={saving || !nameDirty} data-testid="save-profile-btn">Save changes</Button>
              </form>

              <form onSubmit={changePass} className="space-y-4 rounded-xl border border-border bg-surface p-6 shadow-card" data-testid="password-form">
                <div className="eyebrow">
                  {hasEmailLogin ? "Password" : "Set a password"}
                </div>
                {!hasEmailLogin && (
                  <p className="text-sm text-muted-foreground">
                    You signed in with Google. Set a password to also log in with your email and password.
                  </p>
                )}
                <div className="max-w-sm">
                  <Label htmlFor="next">{hasEmailLogin ? "New password (min 6 characters)" : "Password (min 6 characters)"}</Label>
                  <Input
                    id="next"
                    type="password"
                    required
                    minLength={6}
                    value={next}
                    onChange={(e) => setNext(e.target.value)}
                    data-testid="new-password-input"
                    className="mt-1"
                    placeholder="••••••••"
                  />
                </div>
                <Button type="submit" disabled={saving} data-testid="change-password-btn">
                  {hasEmailLogin ? "Update password" : "Set password"}
                </Button>
              </form>

              <div className="space-y-3 rounded-xl border border-border bg-surface p-6 shadow-card" data-testid="appearance-card">
                <div className="eyebrow">Appearance</div>
                <p className="text-sm text-muted-foreground">Choose how Inbound Labs looks on this device.</p>
                <ThemeChoice />
              </div>
            </TabsContent>

            {/* ── Workspace ── */}
            <TabsContent value="workspace" className="mt-6 space-y-4">
              <form onSubmit={saveProfile} className="space-y-4 rounded-xl border border-border bg-surface p-6 shadow-card" data-testid="workspace-form">
                <div className="eyebrow">Workspace</div>
                <div>
                  <Label htmlFor="company">Workspace name</Label>
                  <Input id="company" value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Acme GTM" data-testid="workspace-name-input" />
                </div>
                <Button type="submit" disabled={saving || !companyDirty} data-testid="save-workspace-btn">Save workspace</Button>
              </form>
              <div className="rounded-xl border border-border bg-surface p-6 shadow-card">
                <div className="eyebrow">Team</div>
                <p className="mt-2 text-sm text-muted-foreground">
                  Invite teammates to collaborate on research and enrichment lists.
                </p>
                <Button variant="outline" className="mt-3" disabled data-testid="invite-team-btn">Invite teammate (coming soon)</Button>
              </div>
            </TabsContent>

            {/* ── Plan & Usage ── */}
            <TabsContent value="plan" className="mt-6 space-y-4">
              <div className="rounded-xl border border-border bg-surface p-6 shadow-card space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="eyebrow">Current plan</div>
                    <div className="mt-1 flex items-center gap-2">
                      <span className="font-display text-xl tracking-tight">{PLAN_LABELS[plan] ?? plan}</span>
                      <Badge variant={plan === "free" ? "secondary" : "default"} className="capitalize">{plan}</Badge>
                    </div>
                  </div>
                  {plan === "free" && (
                    <Button asChild data-testid="upgrade-plan-btn">
                      <Link to="/choose-plan">Upgrade</Link>
                    </Button>
                  )}
                </div>
              </div>

              <div className="rounded-xl border border-border bg-surface p-6 shadow-card space-y-3">
                <div className="eyebrow">Credit usage</div>
                <div className="flex items-end justify-between">
                  <div className="text-2xl font-display tracking-tight">
                    {creditsRemaining}
                    <span className="text-base text-muted-foreground font-sans font-normal"> / {user?.credits_total ?? 0} remaining</span>
                  </div>
                  <div className="text-xs text-muted-foreground">{user?.credits_used ?? 0} used</div>
                </div>
                <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all"
                    style={{
                      width: `${creditsPct}%`,
                      background: creditsPct < 10
                        ? "hsl(var(--warning))"
                        : "linear-gradient(90deg, hsl(var(--primary)), hsl(var(--orchid)))",
                    }}
                  />
                </div>
                {creditsPct < 20 && (
                  <p className="text-xs text-warning">
                    Running low on credits.{" "}
                    <Link to="/choose-plan" className="underline underline-offset-2">
                      Upgrade your plan
                    </Link>{" "}
                    to get more.
                  </p>
                )}
              </div>
            </TabsContent>

            {/* ── API ── */}
            <TabsContent value="api" className="mt-6 space-y-4">
              <div className="rounded-xl border border-border bg-surface p-6 shadow-card">
                <div className="flex items-center gap-2">
                  <div className="eyebrow">API key</div>
                  <Badge variant="secondary" data-testid="api-coming-soon-badge">Coming soon</Badge>
                </div>
                <p className="mt-2 text-sm text-muted-foreground">
                  Programmatic access to the InboundLabs API is on the way. You'll be able to generate and manage keys here.
                </p>
                <div className="mt-3 flex items-center gap-2 opacity-60">
                  <Input readOnly disabled value="il_••••••••••••••••••••••••" className="font-mono-bd text-xs" data-testid="api-key-display" />
                  <Button variant="outline" size="icon" disabled data-testid="copy-api-key-btn"><Copy className="h-4 w-4" /></Button>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
